import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import type { Query } from '@tanstack/react-query';

import { customerQueries } from '@common/queries/customer';
import { debtsQueries } from '@common/queries/debts';
import { scoreQueries } from '@common/queries/score';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 0,
      gcTime: 1000 * 60 * 10, // 10 minutes
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: {
    getItem: (key) => {
      try {
        return window.localStorage?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    setItem: (key, value) => {
      try {
        window.localStorage?.setItem(key, value);
      } catch {}
    },
    removeItem: (key) => {
      try {
        window.localStorage?.removeItem(key);
      } catch {}
    },
  },
  key: 'cp_queries',
});

const buster = import.meta.env.VITE_DEPLOY_VERSION;

const nonPersistedQueries = [
  debtsQueries.all().queryKey,
  scoreQueries.cpfLookout().queryKey,
  scoreQueries.history().queryKey,
  scoreQueries.negative().queryKey,
  scoreQueries.objectives().queryKey,
  scoreQueries.score().queryKey,
  customerQueries.all().queryKey,
];

const dehydrateOptions = {
  shouldDehydrateQuery: (query: Query) => {
    const queryKeyString = JSON.stringify(query.queryKey);

    const nonPersistedQueriesStrings = nonPersistedQueries.map((q) => JSON.stringify(q));

    if (nonPersistedQueriesStrings.includes(queryKeyString)) {
      return false;
    }

    return query.state.status === 'success';
  },
};

export const persistConfig = {
  buster,
  dehydrateOptions,
  persister,
};
